import React, { useState, useMemo, useEffect, useRef } from "react";
import {
  Table,
  TableContainer,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Typography,
  Box,
  IconButton,
  Tooltip,
  useTheme,
  TableSortLabel,
  Button,
} from "@mui/material";
import {
  DeleteOutlined as DeleteIcon,
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
} from "@mui/icons-material";
import { colors, borderRadius } from "../../../styles/theme";
import ExpandableRowDetail from "./ExpandableRowDetail";

/** Dauer, bis eine aufgeklappte Zeile zugeklappt ist (`ExpandableRowDetail`), plus Puffer */
const COLLAPSE_DURATION_MS = 220;
import DialogTemplate from "../../layout/DialogTemplate";
import InfoTooltip from "../../common/overlays/InfoTooltip";
import AbgeschnittenerInhalt from "../../common/display/AbgeschnittenerInhalt";

// Typen für Tabellenspalten
export interface TableColumn {
  key: string;
  label: string;
  align?: "left" | "center" | "right";
  width?: number | string;
  sortable?: boolean;
  collapsible?: boolean;
  sticky?: boolean;
  showRightBorder?: boolean;
  showLeftBorder?: boolean;
  isDetailColumn?: boolean;
  unit?: string;
  // Neue Eigenschaften für Haupt-/Unterspalten-Beziehungen
  isMainColumn?: boolean; // Hauptspalte, die Unterspalten hat
  parentColumn?: string; // Schlüssel der übergeordneten Hauptspalte
  columnGroup?: string; // Gruppe, zu der die Spalte gehört
  /** Erklärungstext, der als Info-Icon mit Tooltip neben der Spaltenüberschrift erscheint */
  headerTooltip?: React.ReactNode;
  /** Zusätzliche Styles für Header- und Datenzellen dieser Spalte (z.B. für aufklappbare Unterspalten) */
  sx?: Record<string, any>;
  /** Liefert den zum Sortieren verwendeten Wert einer Zeile, falls der Zellwert selbst nicht direkt vergleichbar ist (z.B. Objekt) */
  sortAccessor?: (row: TableData) => string | number;
  /**
   * Zweite, hellgraue Zeile unter dem Zellinhalt – für eine Einordnung, die keine eigene Spalte
   * verdient (z. B. die Nachweisart unter der Bezeichnung). Einheitliches Muster für alle
   * Tabellen: hier konfigurieren, nicht im `renderCell` nachbauen. Leer = keine Zeile. Meist
   * Text; ein Element, wenn ein Teil davon einen Tooltip oder Klick braucht.
   */
  untertitel?: (row: TableData) => React.ReactNode;
}

// Typen für Tabellendaten
export interface TableData {
  id: string;
  [key: string]: any;
}

// Props für BaseTable
export interface BaseTableProps {
  columns: TableColumn[];
  data: TableData[];
  stickyHeader?: boolean;
  maxHeight?: number | string;
  minWidth?: number;
  /**
   * Feste Spaltenbreiten (`table-layout: fixed`). Ohne das ignoriert der Browser die
   * Prozentangaben der Spalten und richtet sie am breitesten Zelleninhalt aus – eine lange
   * Bezeichnung zieht die Tabelle dann über ihren Container hinaus. Standard bleibt `false`,
   * damit bestehende Tabellen ihr Verhalten behalten.
   */
  fixedLayout?: boolean;
  /**
   * Teilt die Zeilen im **Ausgangszustand** in Blöcke: Die Funktion liefert je Zeile einen
   * Rang, kleinere Ränge stehen oben, und zwischen zwei Rängen zieht die Tabelle eine
   * Trennlinie. Innerhalb eines Blocks bleibt die Voreinstellungs-Sortierung erhalten.
   *
   * Sobald der Anwender selbst sortiert, entfällt die Aufteilung samt Linie – er hat dann
   * eine Ordnung verlangt, und eine Vorgruppierung würde sie stillschweigend überstimmen.
   */
  defaultGroupRank?: (row: TableData) => number;
  showBorder?: boolean;
  variant?: "default" | "compact" | "detailed";
  onRowClick?: (row: TableData) => void;
  selectedRowId?: string;
  loading?: boolean;
  emptyMessage?: string;
  renderCell?: (
    column: TableColumn,
    row: TableData,
    value: any,
  ) => React.ReactNode;
  renderHeader?: (column: TableColumn) => React.ReactNode;
  renderActions?: (row: TableData) => React.ReactNode;
  /** Callback zum Löschen einer Zeile – rendert automatisch den Standard-Löschen-Button */
  onDelete?: (row: TableData, e: React.MouseEvent) => void;
  /** Zeigt einen Bestätigungsdialog vor dem Löschen */
  confirmDelete?: boolean;
  /** Nachricht im Bestätigungsdialog (Standard: "Eintrag wirklich löschen?") */
  confirmDeleteMessage?: (row: TableData) => string;
  actionsColumnWidth?: number | string;
  actionsColumnAlign?: "left" | "center" | "right";
  /**
   * Überschrift der Aktionsspalte (Standard: "Aktionen"). Leerer Text lässt sie weg – für
   * Tabellen, in denen eine einzelne, selbsterklärende Aktion steht und die Überschrift nur
   * Breite kostet. Die Spalte selbst bleibt.
   */
  actionsColumnLabel?: string;
  defaultSortColumn?: string;
  defaultSortDirection?: "asc" | "desc";
  /** Rendert expandierten Inhalt für eine Zeile (wenn gesetzt, werden Zeilen expandierbar) */
  renderExpandedRow?: (row: TableData) => React.ReactNode;
  /** ID der aktuell expandierten Zeile */
  expandedRowId?: string | null;
  /** Callback wenn eine Zeile expandiert/kollabiert wird */
  onExpandRow?: (rowId: string | null) => void;
  /** Optionale zusätzliche Styles pro Zeile (z.B. farbige Ränder) */
  getRowSx?: (row: TableData) => Record<string, any>;
  /**
   * Unterzeilen der aufgeklappten Zeile: Sie stehen unter ihr in denselben Spalten, wie in
   * einer Baumtabelle, jede mit eigenem Hover. Was in ihren Zellen steht, liefert
   * `renderSubRowCell`. Gibt es welche, entfällt `renderExpandedRow` für diese Zeile nicht von
   * selbst – der Aufrufer liefert dort dann `null`.
   */
  getSubRows?: (row: TableData) => TableData[];
  renderSubRowCell?: (
    column: TableColumn,
    subRow: TableData,
    row: TableData,
  ) => React.ReactNode;
  /** Inhalt der Aktionsspalte einer Unterzeile */
  renderSubRowActions?: (subRow: TableData, row: TableData) => React.ReactNode;
  /** Zusätzliche Styles je Unterzeile, etwa für das Hervorheben einer ganzen Gruppe */
  getSubRowSx?: (subRow: TableData, row: TableData) => Record<string, any>;
  /** Zeilen pro Seite für Pagination bzw. Chunk-Größe für Infinite Scroll (Default: 10). */
  pageSize?: number;
  /** Pagination komplett deaktivieren (z.B. für CollapsibleTable / GuV) */
  disablePagination?: boolean;
  /** "pages" (Standard, nummerierte Seiten) oder "infinite-scroll" (Zeilen werden beim Scrollen automatisch nachgeladen) */
  paginationMode?: "pages" | "infinite-scroll";
  /**
   * Wohin eine angesprungene Zeile scrollt: `nearest` holt nur Verdecktes in den sichtbaren
   * Bereich (Standard), `start` setzt sie an den oberen Rand – unter den festen Kopf.
   */
  scrollRowTo?: "nearest" | "start";
  /**
   * Mit ↑/↓ zur vorigen bzw. nächsten Zeile wechseln, solange eine aufgeklappt ist, und mit Esc
   * zuklappen – in der Reihenfolge, in der die Tabelle sortiert ist. Braucht `onExpandRow`.
   */
  keyboardNavigation?: boolean;
}

/**
 * Einheitliches Tabellen-Template für alle Tabellen in der Anwendung
 */
const BaseTable: React.FC<BaseTableProps> = ({
  columns,
  data,
  stickyHeader = false,
  maxHeight,
  minWidth = 650,
  fixedLayout = false,
  defaultGroupRank,
  showBorder = true,
  variant = "default",
  onRowClick,
  selectedRowId,
  loading = false,
  emptyMessage = "Keine Daten verfügbar",
  renderCell,
  renderHeader,
  renderActions,
  onDelete,
  confirmDelete = false,
  confirmDeleteMessage,
  actionsColumnWidth,
  actionsColumnAlign = "right",
  actionsColumnLabel = "Aktionen",
  defaultSortColumn,
  defaultSortDirection = "asc",
  renderExpandedRow,
  expandedRowId,
  onExpandRow,
  getRowSx,
  getSubRows,
  renderSubRowCell,
  renderSubRowActions,
  getSubRowSx,
  pageSize = 10,
  disablePagination = false,
  paginationMode = "pages",
  scrollRowTo = "nearest",
  keyboardNavigation = false,
}) => {
  const theme = useTheme();
  const hasActions = !!(renderActions || onDelete);

  // Sortier-State
  const [sortColumn, setSortColumn] = useState<string | null>(
    defaultSortColumn || null,
  );
  const [sortDirection, setSortDirection] = useState<"asc" | "desc">(
    defaultSortDirection,
  );
  // Erst ein Klick auf eine Spaltenüberschrift hebt die Vorgruppierung auf – nicht schon
  // die Voreinstellung, die ja ebenfalls eine Sortierspalte setzt.
  const [sortierungGeaendert, setSortierungGeaendert] = useState(false);

  // Pagination-State
  const [page, setPage] = useState(0);
  // Infinite-Scroll-State: Anzahl aktuell gerenderter Zeilen
  const [visibleCount, setVisibleCount] = useState(pageSize);
  const [pendingScrollId, setPendingScrollId] = useState<string | null>(null);
  const rowRefs = useRef<Map<string, HTMLTableRowElement>>(new Map());
  const sentinelRef = useRef<HTMLDivElement | null>(null);
  const headRef = useRef<HTMLTableSectionElement | null>(null);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const scrollTimerRef = useRef<number | null>(null);

  // Bestätigungsdialog-State
  const [confirmRow, setConfirmRow] = useState<TableData | null>(null);

  const handleDeleteClick = (row: TableData, e: React.MouseEvent) => {
    e.stopPropagation();
    if (confirmDelete) {
      setConfirmRow(row);
    } else {
      onDelete?.(row, e);
    }
  };

  const handleConfirmDelete = (e: React.MouseEvent) => {
    if (confirmRow && onDelete) {
      onDelete(confirmRow, e);
    }
    setConfirmRow(null);
  };

  // Sortier-Funktion
  const sortData = (
    dataToSort: TableData[],
    columnKey: string,
    direction: "asc" | "desc",
  ): TableData[] => {
    const sortAccessor = columns.find((c) => c.key === columnKey)?.sortAccessor;
    return [...dataToSort].sort((a, b) => {
      const aValue = sortAccessor ? sortAccessor(a) : a[columnKey];
      const bValue = sortAccessor ? sortAccessor(b) : b[columnKey];

      // Null/undefined am Ende sortieren
      if (aValue === null || aValue === undefined) return 1;
      if (bValue === null || bValue === undefined) return -1;

      let comparison = 0;

      // Typ-basierte Sortierung
      if (typeof aValue === "number" && typeof bValue === "number") {
        comparison = aValue - bValue;
      } else if (typeof aValue === "string" && typeof bValue === "string") {
        // Case-insensitive String-Vergleich mit localeCompare für deutsche Sortierung
        comparison = aValue.localeCompare(bValue, "de", {
          sensitivity: "base",
          numeric: true,
        });
      } else {
        // Gemischte Typen: zu String konvertieren
        comparison = String(aValue).localeCompare(String(bValue), "de", {
          sensitivity: "base",
          numeric: true,
        });
      }

      return direction === "asc" ? comparison : -comparison;
    });
  };

  // Sortierte Daten
  const sortedData = useMemo(() => {
    const sortiert = sortColumn
      ? sortData(data, sortColumn, sortDirection)
      : data;
    if (!defaultGroupRank || sortierungGeaendert) return sortiert;
    // Stabil: Array.prototype.sort erhält die Reihenfolge gleicher Ränge, innerhalb eines
    // Blocks bleibt die Sortierung von oben also bestehen.
    return [...sortiert].sort(
      (a, b) => defaultGroupRank(a) - defaultGroupRank(b),
    );
  }, [data, sortColumn, sortDirection, defaultGroupRank, sortierungGeaendert]);

  /** True, wenn vor dieser Zeile die Trennlinie zwischen zwei Blöcken steht. */
  const beginntNeuenBlock = (row: TableData, index: number): boolean => {
    if (!defaultGroupRank || sortierungGeaendert || index === 0) return false;
    return defaultGroupRank(sortedData[index - 1]) !== defaultGroupRank(row);
  };

  // Page/Chunk zurücksetzen wenn sich die Daten ändern
  useEffect(() => {
    setPage(0);
    setVisibleCount(pageSize);
  }, [data, pageSize]);

  /**
   * Zuletzt angesprungene Auswahl. Gescrollt wird nur, wenn sich die Auswahl ändert – nicht bei
   * jedem Neuladen der Daten, sonst spränge die Tabelle nach jedem Speichern zurück.
   */
  const angesprungeneId = useRef<string | null>(null);

  // Zur ausgewählten/expandierten Zeile springen (Seite wechseln bzw. genug Zeilen nachladen) und
  // dorthin scrollen. Einheitliches Muster für jede Auswahl von außen – Karte, Zeitstrahl:
  // Die Seite hält die ID, die Tabelle markiert die Zeile und holt sie in den sichtbaren Bereich.
  useEffect(() => {
    const targetId = expandedRowId ?? selectedRowId ?? null;
    if (!targetId) {
      angesprungeneId.current = null;
      return;
    }
    const rowIndex = sortedData.findIndex((row) => row.id === targetId);
    if (rowIndex < 0) return;
    if (!disablePagination) {
      if (paginationMode === "infinite-scroll") {
        setVisibleCount((prev) => (rowIndex >= prev ? rowIndex + 1 : prev));
      } else {
        setPage(Math.floor(rowIndex / pageSize));
      }
    }
    if (angesprungeneId.current !== targetId) {
      angesprungeneId.current = targetId;
      setPendingScrollId(targetId);
    }
  }, [
    expandedRowId,
    selectedRowId,
    sortedData,
    pageSize,
    disablePagination,
    paginationMode,
  ]);

  // Paginierte bzw. für Infinite Scroll sichtbare Daten
  const pagedData = useMemo(() => {
    if (disablePagination) return sortedData;
    if (paginationMode === "infinite-scroll") {
      return sortedData.slice(0, visibleCount);
    }
    return sortedData.slice(page * pageSize, (page + 1) * pageSize);
  }, [
    sortedData,
    page,
    pageSize,
    disablePagination,
    paginationMode,
    visibleCount,
  ]);

  // Sobald die Zielzeile gerendert ist, dorthin scrollen
  useEffect(() => {
    if (!pendingScrollId) return;
    const node = rowRefs.current.get(pendingScrollId);
    if (node) {
      const container = containerRef.current;
      if (scrollRowTo === "start" && container && maxHeight) {
        // "start": Die Zeile rückt an den oberen Rand des eigenen Scrollbereichs, unter den
        // festen Kopf. Nur der Bereich scrollt, nicht die Seite – sonst rutschte, was über der
        // Tabelle steht, mit aus dem Bild.
        // Gemessen wird erst, wenn die zuvor aufgeklappte Zeile zugeklappt ist – sie verschiebt
        // die Zielzeile sonst noch, während schon gescrollt wird.
        const headerHeight = stickyHeader
          ? (headRef.current?.offsetHeight ?? 0)
          : 0;
        // Nur der letzte Ansprung zählt – wer schnell weiterblättert, soll nicht zurückspringen.
        if (scrollTimerRef.current !== null)
          window.clearTimeout(scrollTimerRef.current);
        scrollTimerRef.current = window.setTimeout(() => {
          scrollTimerRef.current = null;
          container.scrollTo({
            top:
              container.scrollTop +
              node.getBoundingClientRect().top -
              container.getBoundingClientRect().top -
              headerHeight,
            behavior: "smooth",
          });
        }, COLLAPSE_DURATION_MS);
      } else {
        // "nearest": Eine schon sichtbare Zeile bleibt, wo sie ist – nur Verdecktes wird geholt.
        node.scrollIntoView({ behavior: "smooth", block: "nearest" });
      }
      setPendingScrollId(null);
    }
  }, [pendingScrollId, pagedData, scrollRowTo, stickyHeader, maxHeight]);

  useEffect(
    () => () => {
      if (scrollTimerRef.current !== null)
        window.clearTimeout(scrollTimerRef.current);
    },
    [],
  );

  // ↑/↓ wechselt die aufgeklappte Zeile, Esc klappt zu. Nicht, solange man tippt, auf der Karte
  // steht oder ein Dialog, Menü oder eine Auswahlliste offen ist – dort gehören die Tasten dem
  // Feld, der Karte bzw. dem Overlay.
  useEffect(() => {
    if (!keyboardNavigation || !onExpandRow || !expandedRowId) return;
    const handleKey = (event: KeyboardEvent) => {
      const target = event.target as HTMLElement | null;
      if (
        event.defaultPrevented ||
        target?.closest(
          "input, textarea, select, [contenteditable='true'], [role='listbox'], [role='menu'], .leaflet-container",
        ) ||
        document.querySelector(
          ".MuiDialog-root, .MuiPopover-root, .MuiMenu-root, .MuiAutocomplete-popper",
        )
      ) {
        return;
      }
      if (event.key === "Escape") {
        onExpandRow(null);
        return;
      }
      if (event.key !== "ArrowDown" && event.key !== "ArrowUp") return;
      const index = sortedData.findIndex((row) => row.id === expandedRowId);
      const next = sortedData[index + (event.key === "ArrowDown" ? 1 : -1)];
      if (index < 0 || !next) return;
      event.preventDefault();
      onExpandRow(String(next.id));
    };
    window.addEventListener("keydown", handleKey);
    return () => window.removeEventListener("keydown", handleKey);
  }, [keyboardNavigation, onExpandRow, expandedRowId, sortedData]);

  const hasMoreToLoad =
    paginationMode === "infinite-scroll" &&
    !disablePagination &&
    visibleCount < sortedData.length;

  // Beobachtet den Sentinel am Tabellenende und lädt beim Sichtbarwerden den nächsten Chunk nach
  useEffect(() => {
    if (!hasMoreToLoad) return;
    const node = sentinelRef.current;
    if (!node) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          setVisibleCount((prev) =>
            Math.min(prev + pageSize, sortedData.length),
          );
        }
      },
      { rootMargin: "200px" },
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, [hasMoreToLoad, pageSize, sortedData.length]);

  const handleSort = (columnKey: string) => {
    setPage(0);
    setSortierungGeaendert(true);
    if (sortColumn === columnKey) {
      // Gleiche Spalte: Richtung umkehren
      setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    } else {
      // Neue Spalte: aufsteigend sortieren
      setSortColumn(columnKey);
      setSortDirection("asc");
    }
  };

  // Einheitliche Styles basierend auf Variante
  const getTableStyles = () => {
    const baseStyles = {
      minWidth,
      ...(fixedLayout
        ? {
            tableLayout: "fixed" as const,
            // Zu breite Inhalte werden abgeschnitten statt die Spalte aufzuziehen. Die
            // Zell-Komponenten setzen dafür bereits `noWrap`.
            "& .MuiTableCell-root": {
              overflow: "hidden",
              textOverflow: "ellipsis",
            },
          }
        : {}),
      "& .MuiTableCell-stickyHeader": {
        backgroundColor: colors.background.tableHeader,
        borderBottom: "2px solid",
        borderBottomColor: "divider",
        fontWeight: 600,
        fontSize: "0.875rem",
      },
      "& .MuiTableBody-root .MuiTableRow-root.Mui-selected": {
        backgroundColor: colors.active.background,
        "& .MuiTableCell-root": {
          backgroundColor: colors.active.background,
        },
      },
      // Der äußere Container clippt die Tabelle auf abgerundete Ecken (overflow: hidden),
      // die Zell-Hintergründe von Selektion/Hover bleiben aber rechteckig. Ohne explizite
      // Rundung an genau den vier äußeren Zellen bleibt an der ersten/letzten Zeile eine
      // eckige Ecke sichtbar, sobald deren Hintergrund gefüllt ist.
      "& .MuiTableBody-root .MuiTableRow-root:last-of-type": {
        "& .MuiTableCell-root:first-of-type": {
          borderBottomLeftRadius: borderRadius.md * 2,
        },
        "& .MuiTableCell-root:last-of-type": {
          borderBottomRightRadius: borderRadius.md * 2,
        },
      },
      "& .MuiTableHead-root .MuiTableRow-root:first-of-type": {
        "& .MuiTableCell-root:first-of-type": {
          borderTopLeftRadius: borderRadius.md * 2,
        },
        "& .MuiTableCell-root:last-of-type": {
          borderTopRightRadius: borderRadius.md * 2,
        },
      },
      // Allgemeine Hover-Effekte für alle Body-Zeilen (auf Zellen, nicht Row)
      "& .MuiTableBody-root .MuiTableRow-root": {
        "& .MuiTableCell-root": {
          transition: "background-color 0.2s ease",
        },
        "&:hover .MuiTableCell-root": {
          backgroundColor: colors.state.hoverPrimary,
        },
      },
    };

    // Varianten-spezifische Styles
    switch (variant) {
      case "compact":
        return {
          ...baseStyles,
          "& .MuiTableCell-root": {
            padding: "8px 12px",
            fontSize: "0.8rem",
          },
        };
      case "detailed":
        return {
          ...baseStyles,
          "& .MuiTableCell-root": {
            padding: "16px 20px",
            fontSize: "0.9rem",
          },
        };
      default:
        return {
          ...baseStyles,
          "& .MuiTableCell-root": {
            padding: "12px 16px",
            fontSize: "0.875rem",
          },
        };
    }
  };

  // Standard-Cell-Renderer
  const defaultRenderCell = (
    column: TableColumn,
    row: TableData,
    value: any,
  ) => {
    return (
      <Typography
        variant="body2"
        sx={{ fontWeight: "medium" }}
        color={column.isDetailColumn ? "textSecondary" : "textPrimary"}
      >
        {value}
      </Typography>
    );
  };

  // Loading State
  if (loading) {
    return (
      <Box sx={{ p: 4, textAlign: "center" }}>
        <Typography variant="body2" color="textSecondary">
          Lade Daten...
        </Typography>
      </Box>
    );
  }

  // Empty State
  if (data.length === 0) {
    return (
      <Box sx={{ p: 4, textAlign: "center" }}>
        <Typography variant="body2" color="textSecondary">
          {emptyMessage}
        </Typography>
      </Box>
    );
  }

  // Spaltenüberschrift inkl. optionalem Info-Icon mit Tooltip
  const renderHeaderLabel = (column: TableColumn) => {
    const label = (
      <Typography
        variant="subtitle2"
        sx={{ fontWeight: "bold" }}
        color={column.isDetailColumn ? "textSecondary" : "textPrimary"}
      >
        {column.label}
      </Typography>
    );

    if (!column.headerTooltip) return label;

    return (
      <InfoTooltip title={column.headerTooltip} placement="top">
        {label}
      </InfoTooltip>
    );
  };

  // Umschließt Header-Inhalt (Default oder custom via renderHeader) mit Sortier-Klick und Pfeil-Icon
  const wrapWithSort = (column: TableColumn, content: React.ReactNode) => {
    if (!column.sortable) return content;

    const isActive = sortColumn === column.key;
    const currentDirection = isActive ? sortDirection : "asc";
    const zentriert = column.align === "center";

    const pfeil = (
      <Typography
        component="span"
        className="sort-arrow-icon"
        sx={{
          fontSize: "0.75rem",
          fontWeight: "bold",
          color: isActive ? "primary.main" : "action.disabled",
          opacity: isActive ? 1 : 0,
          transform:
            currentDirection === "asc" ? "rotate(90deg)" : "rotate(-90deg)",
          transition:
            "transform 0.2s ease-in-out, color 0.2s ease-in-out, opacity 0.2s ease-in-out",
          display: "inline-block",
          lineHeight: 1,
          marginLeft: "12px",
          flexShrink: 0,
        }}
      >
        &gt;
      </Typography>
    );

    return (
      <Box
        onClick={() => handleSort(column.key)}
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent:
            column.align === "left"
              ? "flex-start"
              : zentriert
                ? "center"
                : "flex-end",
          width: "100%",
          cursor: "pointer",
          "&:hover .sort-arrow-icon": {
            opacity: 1,
          },
        }}
      >
        {zentriert ? (
          // Zentriert wird nur das Label – der Pfeil hängt außen rechts daran und zählt nicht
          // mit, sonst stünde das Label um die Pfeilbreite links von der Mitte der Inhalte.
          <Box sx={{ position: "relative", display: "inline-flex" }}>
            {content}
            <Box
              sx={{
                position: "absolute",
                left: "100%",
                top: 0,
                bottom: 0,
                display: "flex",
                alignItems: "center",
              }}
            >
              {pfeil}
            </Box>
          </Box>
        ) : (
          <>
            {content}
            {pfeil}
          </>
        )}
      </Box>
    );
  };

  return (
    <>
      <DialogTemplate
        open={confirmRow !== null}
        onClose={() => setConfirmRow(null)}
        title="Löschen bestätigen"
        maxWidth="xs"
        actions={
          <>
            <Button variant="outlined" onClick={() => setConfirmRow(null)}>
              Abbrechen
            </Button>
            <Button
              variant="contained"
              color="error"
              onClick={handleConfirmDelete}
            >
              Löschen
            </Button>
          </>
        }
      >
        <Typography>
          {confirmRow && confirmDeleteMessage
            ? confirmDeleteMessage(confirmRow)
            : "Eintrag wirklich löschen?"}
        </Typography>
      </DialogTemplate>
      <Box sx={{ borderRadius: 2, overflow: "hidden" }}>
        <TableContainer
          ref={containerRef}
          sx={{
            maxHeight,
            border: "none",
            // `minWidth={0}` heißt "diese Tabelle soll nie waagerecht scrollen". Ohne das
            // bliebe trotzdem ein Balken übrig: sobald senkrecht gescrollt wird, nimmt die
            // Bildlaufleiste dem Inhalt ihre Breite weg und die Tabelle ragt genau darum über.
            ...(minWidth === 0 ? { overflowX: "hidden" } : {}),
          }}
        >
          <Table stickyHeader={stickyHeader} sx={getTableStyles()}>
            <TableHead ref={headRef}>
              <TableRow sx={{ backgroundColor: colors.background.tableHeader }}>
                {columns.map((column) => (
                  <TableCell
                    key={column.key}
                    align={column.align || "right"}
                    sx={{
                      width: column.width,
                      position: stickyHeader
                        ? "sticky"
                        : column.sticky
                          ? "sticky"
                          : "static",
                      left: column.sticky ? 0 : "auto",
                      zIndex: stickyHeader ? 3 : column.sticky ? 3 : 1,
                      backgroundColor: stickyHeader
                        ? colors.background.tableHeader
                        : column.sticky
                          ? colors.background.tableHeader
                          : "inherit",
                      borderRight: column.sticky
                        ? `1px solid ${colors.utility.border}`
                        : column.showRightBorder
                          ? `1px solid ${colors.utility.border}`
                          : "none",
                      borderLeft: column.showLeftBorder
                        ? `1px solid ${colors.utility.border}`
                        : "none",
                      cursor: column.sortable ? "pointer" : "default",
                      ...column.sx,
                    }}
                  >
                    {wrapWithSort(
                      column,
                      renderHeader
                        ? renderHeader(column)
                        : renderHeaderLabel(column),
                    )}
                  </TableCell>
                ))}
                {hasActions && (
                  <TableCell
                    align={actionsColumnAlign}
                    sx={actionsColumnWidth ? { width: actionsColumnWidth } : {}}
                  >
                    {actionsColumnLabel && (
                      <Typography
                        variant="subtitle2"
                        sx={{ fontWeight: "bold" }}
                      >
                        {actionsColumnLabel}
                      </Typography>
                    )}
                  </TableCell>
                )}
              </TableRow>
            </TableHead>
            <TableBody>
              {pagedData.map((row, index) => {
                const isExpanded = expandedRowId === row.id;
                const totalColumns = columns.length + (hasActions ? 1 : 0);
                const absoluterIndex = page * pageSize + index;
                const isLastRow = absoluterIndex === sortedData.length - 1;
                // Die Trennlinie sitzt als kräftigerer oberer Rand an der ersten Zeile des
                // zweiten Blocks – keine eigene Zeile, die Auswahl, Löschen-Spalte und
                // aufklappbare Zeilen durcheinanderbrächte.
                const neuerBlock = beginntNeuenBlock(row, absoluterIndex);

                return (
                  <React.Fragment key={row.id}>
                    <TableRow
                      ref={(node: HTMLTableRowElement | null) => {
                        if (node) rowRefs.current.set(row.id, node);
                        else rowRefs.current.delete(row.id);
                      }}
                      selected={selectedRowId === row.id || isExpanded}
                      onClick={() => onRowClick?.(row)}
                      sx={{
                        cursor: onRowClick ? "pointer" : "default",
                        "& td": {
                          borderBottom:
                            isExpanded || isLastRow ? "none" : undefined,
                          ...(neuerBlock
                            ? {
                                borderTop: `2px solid ${colors.border.medium}`,
                              }
                            : {}),
                        },
                        ...getRowSx?.(row),
                      }}
                    >
                      {columns.map((column) => (
                        <TableCell
                          key={column.key}
                          align={column.align || "right"}
                          sx={{
                            position: stickyHeader
                              ? "sticky"
                              : column.sticky
                                ? "sticky"
                                : "static",
                            left: column.sticky ? 0 : "auto",
                            zIndex: stickyHeader ? 2 : column.sticky ? 2 : 1,
                            backgroundColor: column.sticky
                              ? "inherit"
                              : "transparent",
                            borderRight: column.sticky
                              ? `1px solid ${colors.utility.border}`
                              : column.showRightBorder
                                ? `1px solid ${colors.utility.border}`
                                : "none",
                            borderLeft: column.showLeftBorder
                              ? `1px solid ${colors.utility.border}`
                              : "none",
                            ...column.sx,
                          }}
                        >
                          <AbgeschnittenerInhalt>
                            {renderCell
                              ? renderCell(column, row, row[column.key])
                              : defaultRenderCell(column, row, row[column.key])}
                            {column.untertitel?.(row) && (
                              <Typography
                                variant="caption"
                                noWrap
                                sx={{
                                  display: "block",
                                  color: colors.text.secondary,
                                }}
                              >
                                {column.untertitel(row)}
                              </Typography>
                            )}
                          </AbgeschnittenerInhalt>
                        </TableCell>
                      ))}
                      {hasActions && (
                        <TableCell
                          align={actionsColumnAlign}
                          sx={
                            actionsColumnWidth
                              ? { width: actionsColumnWidth }
                              : {}
                          }
                        >
                          <Box
                            sx={{
                              display: "flex",
                              justifyContent: "flex-end",
                              gap: 0.5,
                            }}
                          >
                            {renderActions?.(row)}
                            {onDelete && (
                              <Tooltip title="Löschen">
                                <span>
                                  <IconButton
                                    size="small"
                                    onClick={(e) => handleDeleteClick(row, e)}
                                    sx={{
                                      color: colors.text.secondary,
                                      "&:hover": {
                                        color: colors.status.error.main,
                                      },
                                    }}
                                  >
                                    <DeleteIcon fontSize="small" />
                                  </IconButton>
                                </span>
                              </Tooltip>
                            )}
                          </Box>
                        </TableCell>
                      )}
                    </TableRow>

                    {/* Unterzeilen der aufgeklappten Zeile */}
                    {isExpanded &&
                      getSubRows?.(row).map((subRow) => (
                        <TableRow
                          key={`${row.id}:${subRow.id}`}
                          sx={getSubRowSx?.(subRow, row)}
                        >
                          {columns.map((column) => (
                            <TableCell
                              key={column.key}
                              align={column.align || "right"}
                              sx={{ position: "relative", ...column.sx }}
                            >
                              {renderSubRowCell?.(column, subRow, row)}
                            </TableCell>
                          ))}
                          {hasActions && (
                            <TableCell align={actionsColumnAlign}>
                              {renderSubRowActions?.(subRow, row)}
                            </TableCell>
                          )}
                        </TableRow>
                      ))}

                    {/* Expandierte Zeile – ohne Inhalt keine */}
                    {renderExpandedRow &&
                      (() => {
                        const detail = renderExpandedRow(row);
                        return detail == null ? null : (
                          <ExpandableRowDetail
                            isExpanded={isExpanded}
                            colSpan={totalColumns}
                            onClose={() => onExpandRow?.(null)}
                          >
                            {detail}
                          </ExpandableRowDetail>
                        );
                      })()}
                  </React.Fragment>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
        {paginationMode === "infinite-scroll" && hasMoreToLoad && (
          <Box ref={sentinelRef} sx={{ height: 1 }} />
        )}
        {paginationMode === "pages" &&
          !disablePagination &&
          Math.ceil(data.length / pageSize) >= 2 &&
          (() => {
            const totalPages = Math.ceil(data.length / pageSize);
            return (
              <Box sx={{ display: "flex", justifyContent: "center", py: 1 }}>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 0.75,
                  }}
                >
                  <IconButton
                    size="small"
                    onClick={() => setPage((p) => p - 1)}
                    disabled={page === 0}
                    sx={{
                      p: 0.25,
                      color: page === 0 ? "action.disabled" : "primary.main",
                    }}
                  >
                    <ChevronLeftIcon fontSize="small" />
                  </IconButton>

                  {Array.from({ length: totalPages }, (_, i) => (
                    <Box
                      key={i}
                      onClick={() => setPage(i)}
                      sx={{
                        width: 8,
                        height: 8,
                        borderRadius: "50%",
                        backgroundColor:
                          i === page
                            ? colors.brand.primary
                            : colors.utility.darkGray,
                        cursor: "pointer",
                        transition: "background-color 0.2s",
                        "&:hover": {
                          backgroundColor:
                            i === page
                              ? colors.brand.primaryDark
                              : colors.text.disabled,
                        },
                      }}
                    />
                  ))}

                  <IconButton
                    size="small"
                    onClick={() => setPage((p) => p + 1)}
                    disabled={page >= totalPages - 1}
                    sx={{
                      p: 0.25,
                      color:
                        page >= totalPages - 1
                          ? "action.disabled"
                          : "text.secondary",
                    }}
                  >
                    <ChevronRightIcon fontSize="small" />
                  </IconButton>
                </Box>
              </Box>
            );
          })()}
      </Box>
    </>
  );
};

export default BaseTable;
